import { useState } from 'react'
import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Play, Pause, Ban } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from 'sonner'
import { apiError } from '@/lib/api-client'
import { currentLocale, i18n } from '@/lib/i18n'
import { useMyPermissions } from '@/hooks/use-settings'
import {
  useChiTietChienDich,
  useKhachChienDich,
  useKhachCanChamTay,
  useDieuKhienChienDich,
  useDieuKhienTaiKhoan,
  tyLeChapNhan,
  NHAN_TRANG_THAI,
  type TaiKhoanChienDich,
  type KhachChienDich,
} from '@/hooks/use-zalo-friend-campaigns'

const NHAN_TK: Record<string, string> = {
  get active() { return i18n.t('marketing:friendDetail.accountStatus.active') },
  get paused() { return i18n.t('marketing:friendDetail.accountStatus.paused') },
  get done() { return i18n.t('marketing:friendDetail.accountStatus.done') },
  get blocked() { return i18n.t('marketing:friendDetail.accountStatus.blocked') },
}
const NHAN_KHACH: Record<string, string> = {
  get pending() { return i18n.t('marketing:friendDetail.contactStatus.pending') },
  get invited() { return i18n.t('marketing:friendDetail.contactStatus.invited') },
  get accepted() { return i18n.t('marketing:friendDetail.contactStatus.accepted') },
  get declined() { return i18n.t('marketing:friendDetail.contactStatus.declined') },
  get skipped() { return i18n.t('marketing:friendDetail.contactStatus.skipped') },
  get failed() { return i18n.t('marketing:friendDetail.contactStatus.failed') },
}

export function FriendCampaignDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation('marketing')
  const { data: c, isLoading } = useChiTietChienDich(id)
  const [locTrangThai, setLocTrangThai] = useState<string | undefined>()
  const { data: khach } = useKhachChienDich(id, { status: locTrangThai, take: 200 })
  const { data: chamTay = [] } = useKhachCanChamTay(id)
  const dieuKhien = useDieuKhienChienDich(id!)
  const dieuKhienTk = useDieuKhienTaiKhoan(id!)
  // Chạy / Tạm dừng / Huỷ và nút từng tài khoản: BE đòi friend_campaigns.run → thiếu thì ẩn (BRD §5.3.1).
  const { data: perms } = useMyPermissions()
  const coQuyenChay = !!perms?.has('friend_campaigns.run')

  if (isLoading || !c) return <div className="p-6 text-muted-foreground">{t('friendDetail.loading')}</div>

  const soBan = new Map(c.soBanTheoTaiKhoan.map((x) => [x.channelAccountId, x.soLienHe]))

  async function chay(hanhDong: 'start' | 'pause' | 'cancel') {
    try {
      await dieuKhien.mutateAsync(hanhDong)
      toast.success(
        hanhDong === 'start' ? t('friendDetail.startedToast')
          : hanhDong === 'pause' ? t('friendDetail.pausedToast') : t('friendDetail.cancelledToast'),
      )
    } catch (e) { toast.error(apiError(e)) }
  }

  const cotTaiKhoan: Column<TaiKhoanChienDich>[] = [
    {
      key: 'tk',
      header: t('friendDetail.columns.account'),
      cell: (a) => (
        <div>
          <div className="font-medium">{a.channelAccount?.displayName ?? a.channelAccountId}</div>
          {a.stoppedReason && (
            <div className="text-xs text-amber-600">{a.stoppedReason}</div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: t('friendDetail.columns.status'),
      cell: (a) => (
        <Badge variant={a.status === 'blocked' ? 'destructive' : a.status === 'active' ? 'default' : 'secondary'}>
          {NHAN_TK[a.status] ?? a.status}
        </Badge>
      ),
    },
    {
      key: 'danhBa',
      header: t('friendDetail.columns.currentContacts'),
      align: 'right',
      cell: (a) => <span className="tabular-nums">{(soBan.get(a.channelAccountId) ?? 0).toLocaleString(currentLocale())}</span>,
    },
    {
      key: 'moi',
      header: t('friendDetail.columns.invitedAccepted'),
      align: 'right',
      cell: (a) => (
        <span className="tabular-nums">
          {a.invitedCount.toLocaleString(currentLocale())} / {a.acceptedCount.toLocaleString(currentLocale())}
        </span>
      ),
    },
    {
      key: 'ke',
      header: t('friendDetail.columns.nextInvite'),
      cell: (a) =>
        a.nextInviteAt
          ? <span className="tabular-nums">{dayjs(a.nextInviteAt).format('HH:mm DD/MM')}</span>
          : <span className="text-muted-foreground">—</span>,
    },
    ...(coQuyenChay ? [{
      key: 'act',
      header: '',
      align: 'right' as const,
      cell: (a: TaiKhoanChienDich) => (
        <Button
          size="sm"
          variant="outline"
          disabled={a.status === 'done'}
          onClick={async () => {
            try {
              await dieuKhienTk.mutateAsync({
                accountId: a.channelAccountId,
                hanhDong: a.status === 'active' ? 'pause' : 'resume',
              })
            } catch (e) { toast.error(apiError(e)) }
          }}
        >
          {a.status === 'active' ? t('friendDetail.pauseAccount') : t('friendDetail.resumeAccount')}
        </Button>
      ),
    }] : []),
  ]

  const cotKhach: Column<KhachChienDich>[] = [
    { key: 'ten', header: t('friendDetail.columns.contact'), cell: (k) => k.displayName || '—' },
    { key: 'sdt', header: t('friendDetail.columns.phone'), cell: (k) => <span className="tabular-nums">{k.phone}</span> },
    {
      key: 'st', header: t('friendDetail.columns.status'),
      cell: (k) => (
        <div>
          <Badge variant={k.status === 'accepted' ? 'default' : 'secondary'}>
            {NHAN_KHACH[k.status] ?? k.status}
          </Badge>
          {k.errorMessage && <div className="mt-1 text-xs text-muted-foreground">{k.errorMessage}</div>}
        </div>
      ),
    },
    {
      key: 'tin', header: t('friendDetail.columns.welcomeMsg'), align: 'right',
      cell: (k) => k.overflowedAt
        ? <span className="text-amber-600">{t('friendDetail.needsManualCare')}</span>
        : <span className="tabular-nums">{k.welcomeSentCount}/2</span>,
    },
  ]

  const tl = tyLeChapNhan(c)

  return (
    <div className="space-y-6">
      <PageHeader
        title={c.name}
        description={t('friendDetail.pageDescription', {
          status: NHAN_TRANG_THAI[c.status], total: c.totalCount.toLocaleString(currentLocale()),
          accounts: c.accounts.length, limit: c.dailyLimit, start: c.sendWindowStart, end: c.sendWindowEnd,
        })}
        actions={
          coQuyenChay && (
            <div className="flex gap-2">
              {c.status !== 'running' && c.status !== 'cancelled' && (
                <Button onClick={() => chay('start')}><Play className="h-4 w-4" /> {t('friendDetail.run')}</Button>
              )}
              {c.status === 'running' && (
                <Button variant="outline" onClick={() => chay('pause')}>
                  <Pause className="h-4 w-4" /> {t('friendDetail.pause')}
                </Button>
              )}
              {c.status !== 'cancelled' && (
                <Button variant="outline" onClick={() => chay('cancel')}>
                  <Ban className="h-4 w-4" /> {t('friendDetail.cancel')}
                </Button>
              )}
            </div>
          )
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {[
          [t('friendDetail.stats.invited'), c.invitedCount],
          [t('friendDetail.stats.accepted'), c.acceptedCount],
          [t('friendDetail.stats.skipped'), c.skippedCount],
          [t('friendDetail.stats.failed'), c.failedCount],
        ].map(([nhan, v]) => (
          <div key={nhan as string} className="rounded-lg border p-4">
            <div className="text-sm text-muted-foreground">{nhan}</div>
            <div className="text-2xl font-semibold tabular-nums">
              {(v as number).toLocaleString(currentLocale())}
            </div>
          </div>
        ))}
        <div className="rounded-lg border p-4">
          <div className="text-sm text-muted-foreground">{t('friendDetail.stats.acceptRate')}</div>
          <div className={`text-2xl font-semibold tabular-nums ${c.invitedCount > 0 && tl < 0.26 ? 'text-amber-600' : ''}`}>
            {c.invitedCount > 0 ? `${(tl * 100).toFixed(1)}%` : '—'}
          </div>
          {c.invitedCount > 0 && tl < 0.26 && (
            <div className="mt-1 text-xs text-amber-600">{t('friendDetail.lowAcceptRateWarning')}</div>
          )}
        </div>
      </div>

      <Tabs defaultValue="taikhoan">
        <TabsList>
          <TabsTrigger value="taikhoan">{t('friendDetail.tabs.accounts', { count: c.accounts.length })}</TabsTrigger>
          <TabsTrigger value="khach">{t('friendDetail.tabs.contacts')}</TabsTrigger>
          <TabsTrigger value="chamtay">{t('friendDetail.tabs.manualCare', { count: chamTay.length })}</TabsTrigger>
        </TabsList>

        <TabsContent value="taikhoan">
          <DataTable columns={cotTaiKhoan} rows={c.accounts} rowKey={(a) => a.id}
            emptyTitle={t('friendDetail.noAccounts')} />
        </TabsContent>

        <TabsContent value="khach" className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={!locTrangThai ? 'default' : 'outline'}
              onClick={() => setLocTrangThai(undefined)}>{t('friendDetail.all')}</Button>
            {Object.entries(NHAN_KHACH).map(([v, nhan]) => (
              <Button key={v} size="sm" variant={locTrangThai === v ? 'default' : 'outline'}
                onClick={() => setLocTrangThai(v)}>{nhan}</Button>
            ))}
          </div>
          <DataTable columns={cotKhach} rows={khach?.rows ?? []} rowKey={(k) => k.id}
            emptyTitle={t('friendDetail.noContactsInStatus')} />
        </TabsContent>

        <TabsContent value="chamtay" className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t('friendDetail.manualCareHint')}
          </p>
          <DataTable columns={cotKhach} rows={chamTay} rowKey={(k) => k.id}
            emptyTitle={t('friendDetail.noManualCare')} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
